import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';

const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  useEffect(() => {
    verifyInvitation();
  }, [token]);
  
  const verifyInvitation = async () => {
    setLoading(true);
    try {
      const response = await api.invitations.verify(token);
      if (response.data.valid) {
        setInviteData(response.data);
      } else {
        setError(response.data.message || 'Invalid invitation');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      console.error('Invitation verification error:', detail, err);
      if (detail === 'Invalid invitation link') {
        setError('This invitation link is invalid or has already been used. Please contact your project administrator for a new invitation.');
      } else if (detail?.includes('User account not found')) {
        setError('The user account associated with this invitation was not found. Please contact your administrator.');
      } else {
        setError(detail || 'Failed to verify invitation. Please try again or contact support.');
      }
    }
    setLoading(false);
  };
  
  const validatePassword = () => {
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(password)) {
      toast.error('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(password)) {
      toast.error('Password must contain at least one number');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    if (!acceptTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.invitations.accept({
        token,
        password,
        confirm_password: confirmPassword,
        accept_terms: acceptTerms
      });
      setSuccess(true);
      toast.success('Account activated successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate account');
    }
    setSubmitting(false);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p className="text-lg">Verifying invitation...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          {error.includes('expired') ? (
            <AlertTriangle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          ) : (
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {error.includes('expired') ? 'Invitation Expired' : 'Invalid Invitation'}
          </h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login
          </Button>
          <p className="text-sm text-slate-500 mt-4">
            Need help? Contact your project administrator.
          </p>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Account Activated!</h1>
          <p className="text-slate-600 mb-6">
            Your account has been set up successfully. Check your email for login details.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            Redirecting to login...
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to Login Now
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Accept Invitation</h1>
          <p className="text-slate-600">
            Welcome to <strong>{inviteData?.project_name || 'the project'}</strong>!
          </p>
        </div>
        
        {/* User Info */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-slate-500">Name:</span>
              <p className="font-medium">
                {inviteData?.user?.first_name} {inviteData?.user?.last_name}
              </p>
            </div>
            <div>
              <span className="text-slate-500">Email:</span>
              <p className="font-medium">{inviteData?.user?.email}</p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Create Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a secure password"
                required
                data-testid="accept-invite-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Min 8 characters, including uppercase, lowercase, and number
            </p>
          </div>
          
          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                data-testid="accept-invite-confirm-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          {/* Terms */}
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox
              id="terms"
              checked={acceptTerms}
              onCheckedChange={setAcceptTerms}
              data-testid="accept-invite-terms"
            />
            <label htmlFor="terms" className="text-sm text-slate-600 leading-tight cursor-pointer">
              I accept the <a href="/terms" className="text-orange-600 hover:underline">Terms of Service</a> and{' '}
              <a href="/privacy" className="text-orange-600 hover:underline">Privacy Policy</a>.
              I agree to maintain confidentiality of project information.
            </label>
          </div>
          
          {/* Submit */}
          <Button
            type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600"
            disabled={submitting}
            data-testid="accept-invite-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Activating Account...
              </>
            ) : (
              'Activate My Account'
            )}
          </Button>
        </form>
        
        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account?{' '}
          <a href="/login" className="text-orange-600 hover:underline">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
